<template>
  <div
    class="box-border w-full bg-[#F5F6F7]"
    :class="isPC ? 'py-[40px] px-[40px]' : 'mx-[auto] w-[680px] px-[24px] py-[48px] rounded-[20px]'"
  >
    <!-- 顶部：状态标签 + 参与人数 -->
    <PredictEventHeader :status="status" :participant-count="participantCount" />

    <!-- 双选 PC：白色内卡片包裹标题+按钮 -->
    <div
      v-if="isDualOption && isPC"
      class="mt-[40px] rounded-[16px] bg-white px-[60px] py-[40px]"
    >
      <h2 class="text-center font-bold text-[#121212] text-[44px] leading-[50px]">
        {{ $label(title) }}
      </h2>
      <p
        v-if="$label(subtitle)"
        class="text-center text-[#999999] mt-[12px] text-[26px] leading-[30px]"
      >
        {{ $label(subtitle) }}
      </p>
      <DualOptionButtons
        class="mt-[48px]"
        :options="options"
        :selected-code="myVoteCode"
        :settled="revealed"
        :winner-code="winnerCode"
        @select="handleSelect"
      />
    </div>

    <!-- 双选 H5 -->
    <template v-else-if="isDualOption">
      <h2 class="mt-[48px] font-bold text-[#121212] text-[44px] leading-[52px]">
        {{ $label(title) }}
      </h2>
      <p
        v-if="$label(subtitle)"
        class="text-[#999999] mt-[12px] text-[26px] leading-[30px]"
      >
        {{ $label(subtitle) }}
      </p>
      <DualOptionButtons
        class="mt-[48px]"
        :options="options"
        :selected-code="myVoteCode"
        :settled="revealed"
        :winner-code="winnerCode"
        @select="handleSelect"
      />
    </template>

    <!-- 多选 -->
    <template v-else>
      <h2
        class="font-bold text-[#121212]"
        :class="isPC ? 'mt-[40px] text-[44px] leading-[50px]' : 'mt-[48px] text-[44px] leading-[52px]'"
      >
        {{ $label(title) }}
      </h2>
      <p
        v-if="$label(subtitle)"
        class="text-[#999999] mt-[12px] text-[26px] leading-[30px]"
      >
        {{ $label(subtitle) }}
      </p>
      <!-- 多选：进行中未参与时受 showPercentOngoing 配置控制 -->
      <PredictOptionList
        :options="options"
        :show-percent="showPercent"
        :revealed="revealed"
        :selected-code="myVoteCode"
        :winner-code="effectiveWinnerCode"
        @select="handleSelect"
      />
    </template>

    <!-- 页脚 -->
    <PredictFooter
      :class="isPC ? 'mt-[32px]' : 'mt-[50px]'"
      :status="status"
      :deadline="deadline"
      :resolve-at="resolveAt"
      :result="result"
      @countdown-finish="emit('countdown-finish')"
    />
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useLocaleString, useFegoPageInfo } from '@ark/web-shared/use';
import { PREDICT_STATUS } from '../../shared/types';
import type { PredictOption, PredictResult, PredictSession, PredictStatus } from '../../shared/types';
import type { MarketStatsResponse } from '@ark/predict-shared';
import PredictEventHeader from './header.vue';
import PredictOptionList from './option-list.vue';
import DualOptionButtons from './event-dual-buttons.vue';
import PredictFooter from './footer.vue';
import { getPredictResult } from '../utils/predict-result';
import locales from '../locales';
const __sfc_main = {};
__sfc_main.props = {
  session: {
    key: "session",
    required: true,
    type: null
  },
  marketStats: {
    key: "marketStats",
    required: false,
    type: null
  },
  showPercentOngoing: {
    key: "showPercentOngoing",
    required: false,
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const {
    useI18n
  } = fegoUse;
  const $t = useI18n(locales);
  const $label = useLocaleString();
  const {
    isPC
  } = useFegoPageInfo();
  const status = computed<PredictStatus>(() => props.session.status);
  const title = computed(() => props.session.question && props.session.question.title);
  const subtitle = computed(() => props.session.question && props.session.question.desc);
  const participantCount = computed(() => props.session.participantCount);
  const options = computed<PredictOption[]>(() => {
    const raw = props.session.answerSpec && props.session.answerSpec.options;
    if (!raw) return [];
    return raw.map(o => ({
      code: o.code,
      label: o.label,
      percent: o.percent
    }));
  });
  const isDualOption = computed(() => options.value.length === 2);
  const myVoteCode = computed(() => props.session.myVoteCode);
  const winnerCode = computed(() => props.session.correctValue);
  const deadline = computed(() => props.session.deadline);
  const resolveAt = computed(() => props.session.resolveAt);
  const showPercent = computed(() => {
    if (status.value === PREDICT_STATUS.UPCOMING) return false;
    if (status.value === PREDICT_STATUS.ONGOING && myVoteCode.value == null) {
      return !!props.showPercentOngoing;
    }
    return true;
  });
  // 已揭晓结果
  const revealed = computed(() => status.value === PREDICT_STATUS.SETTLED);
  const participated = computed(() => myVoteCode.value != null);
  // 未参与时不高亮获胜项（与设计稿「已结束·未参与」一致）
  const effectiveWinnerCode = computed(() => revealed.value && participated.value ? winnerCode.value : null);
  const result = computed<PredictResult | null>(() => {
    return getPredictResult(revealed.value, props.session.settleStatus);
  });
  function handleSelect(option: PredictOption) {
    emit('select', option);
  }
  return {
    emit,
    $label,
    isPC,
    status,
    title,
    subtitle,
    participantCount,
    options,
    isDualOption,
    myVoteCode,
    winnerCode,
    deadline,
    resolveAt,
    showPercent,
    revealed,
    effectiveWinnerCode,
    result,
    handleSelect
  };
};
__sfc_main.components = Object.assign({
  PredictEventHeader,
  DualOptionButtons,
  PredictOptionList,
  PredictFooter
}, __sfc_main.components);
export default __sfc_main;
</script>
